import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaArrowLeft, FaInfoCircle, FaCalendarAlt, FaChartLine, FaEdit, FaTimes, FaFileAlt, FaDownload, FaPlus, FaClipboardList, FaUserTie, FaUserPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { debounce } from 'lodash';
import './ClassDetails.css';
import configData from '../../configure';
import Timetable from './Timetable';
import Planning from './Planning';
import { useAuth } from "../../AuthContext";
import AcademicDocument from './AcademicDocument';


import { useParams, useNavigate } from 'react-router-dom';

function ClassDetails({ viewingClass, onBack, orgId, token: propToken, axios: propAxios, courseDetailsCache, setCourseDetailsCache, setClasses, classes }) {
  const { classInfo } = useParams();
  const navigate = useNavigate();
  const handleBack = onBack || (() => navigate(-1));

  const { user, userRole, orgId: authOrgId, token: authToken, axios: authAxios, parentOrgId } = useAuth();
  const token = propToken || authToken;
  const axios = propAxios || authAxios;
  const activeOrgId = orgId || authOrgId;
  const effectiveParentOrgId = parentOrgId || user?.parentOrgId || user?.orgDetails?.parentOrgId || '';
  const isSmallOrg = effectiveParentOrgId === 'SMALLORG-001';

  const [activeTab, setActiveTab] = useState('overview');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editClassName, setEditClassName] = useState(viewingClass?.className || '');
  const [editSectionName, setEditSectionName] = useState(viewingClass?.sectionName || '');
  const [editSelectedCourses, setEditSelectedCourses] = useState(viewingClass?.courseIds || []);
  const [courses, setCourses] = useState([]);
  const [editCourseSearchTerm, setEditCourseSearchTerm] = useState('');
  const [updatedClass, setUpdatedClass] = useState(viewingClass || { className: '', sectionName: '', classId: '' });
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState(viewingClass?.courseIds || []);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [courseProgress, setCourseProgress] = useState([]);
  const [classStats, setClassStats] = useState({
    totalCourses: 0,
    totalTopics: 0,
    completedTopics: 0,
    overallProgress: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isFacultyModalOpen, setIsFacultyModalOpen] = useState(false);
  const [selectedCourseForFaculty, setSelectedCourseForFaculty] = useState(null);
  const [faculty, setFaculty] = useState([]);
  const [facultySearchTerm, setFacultySearchTerm] = useState('');
  const [selectedFacultyId, setSelectedFacultyId] = useState('');
  const [coursePlanData, setCoursePlanData] = useState({});
  const [loadingFaculty, setLoadingFaculty] = useState(false);
  const [loadingCoursePlans, setLoadingCoursePlans] = useState(false);
  const [academicYear, setAcademicYear] = useState(viewingClass?.academicYear || '2025-2026');
  const [facultyDetailsCache, setFacultyDetailsCache] = useState({});
  const [loadingFacultyNames, setLoadingFacultyNames] = useState({});
  const coursePlanDataRef = useRef({});

  console.log('ClassDetails Props:', { viewingClass, orgId, token, classes });

  const handleFacultySearchChange = debounce((value) => {
    setFacultySearchTerm(value);
  }, 300);

  // Fetch class details from backend if accessed via route and viewingClass is not provided
  useEffect(() => {
    if (!viewingClass && classInfo && activeOrgId) {
      const fetchClassData = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${activeOrgId}`, {
            headers: { Authorization: token }
          });
          const classesList = response.data || [];
          const targetSlug = decodeURIComponent(classInfo).toLowerCase();
          const classData = classesList.find(cls => {
            const currentSlug = `${cls.className} - ${cls.sectionName || 'General'}`.replace(/\s+/g, '-').toLowerCase();
            return currentSlug === targetSlug;
          });

          if (classData) {
            setUpdatedClass(classData);
            setEditClassName(classData.className || '');
            setEditSectionName(classData.sectionName || '');
            setEditSelectedCourses(classData.courseIds || []);
            setSelectedCourses(classData.courseIds || []);
            setAcademicYear(classData.academicYear || '2025-2026');
          } else {
            toast.error('Class not found.');
          }
        } catch (err) {
          toast.error('Failed to load class details.');
          console.error(err);
        } finally {
          setLoading(false);
        }
      };
      fetchClassData();
    }
  }, [viewingClass, classInfo, activeOrgId, token, axios]);

  useEffect(() => {
    if (viewingClass) {
      setEditSelectedCourses(viewingClass.courseIds || []);
      setSelectedCourses(viewingClass.courseIds || []);
      setUpdatedClass(viewingClass);
    }
  }, [viewingClass]);

  useEffect(() => {
    if (activeTab === 'overview' && updatedClass?.classId) {
      loadOverviewData();
    }
  }, [updatedClass?.classId, activeTab]);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      console.log('Viewing class:', updatedClass);
      const plans = await fetchCoursePlans();
      coursePlanDataRef.current = plans;
      console.log('coursePlanDataRef after fetchCoursePlans:', coursePlanDataRef.current);
      await fetchAssignedCourses();
    } catch (error) {
      console.error('Error loading overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFacultyDetails = async (facultyId) => {
    if (!facultyId || facultyId === 'unknown') {
      return { id: facultyId, name: 'Unknown Faculty' };
    }
    if (facultyDetailsCache[facultyId]) {
      return facultyDetailsCache[facultyId];
    }

    try {
      setLoadingFacultyNames((prev) => ({ ...prev, [facultyId]: true }));
      const response = await axios.get(
        configData.get_identity_identity_users(facultyId),
        {
          headers: {
            Authorization: token,
            accept: 'application/json',
          },
        }
      );

      const facultyDetails = {
        id: response.data.user?.userId || facultyId,
        name: `${response.data.user?.firstName || ''} ${response.data.user?.lastName || ''}`.trim() || 'Unknown Faculty',
      };

      setFacultyDetailsCache((prev) => ({
        ...prev,
        [facultyId]: facultyDetails,
      }));

      return facultyDetails;
    } catch (error) {
      console.error(`Error fetching faculty details for ${facultyId}:`, error.response?.data || error.message);
      return { id: facultyId, name: 'Unknown Faculty' };
    } finally {
      setLoadingFacultyNames((prev) => ({ ...prev, [facultyId]: false }));
    }
  };

  const fetchFaculties = useCallback(async () => {
    if (userRole === 'STUDENT') {
      setFaculty([]);
      return;
    }
    setLoadingFaculty(true);
    try {
      const response = await axios.get(
        `${configData.GET_FACULTIES_BY_ORG}/${orgId}`,
        {
          params: {
            userType: "FACULTY",
            pageSize: 500,
          },
          headers: {
            accept: "application/hal+json",
            Authorization: token,
          },
        }
      );

      if (response.data?.items) {
        const extractedFaculties = response.data.items.map((item) => ({
          id: item.user?.userId || 'unknown',
          firstName: item.user?.firstName || 'N/A',
          lastName: item.user?.lastName || '',
          email: item.user?.email || 'N/A',
          city: item.user?.addressDetails?.[0]?.city || 'N/A',
          dateOfBirth: item.user?.dateOfBirth || 'N/A',
          employeeId: item.facultyInfo?.employeeId || 'N/A',
          designation: item.facultyInfo?.designation || 'N/A',
        }));
        console.log('Extracted faculties:', extractedFaculties);
        setFaculty(extractedFaculties);
      } else {
        setFaculty([]);
        toast.info('No faculty data returned from the server.');
      }
    } catch (error) {
      console.error('Error fetching faculties:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      let userErrorMessage = 'Failed to load faculty list. Please try again.';
      if (error.response?.status === 401) {
        userErrorMessage = 'Authentication failed. Please check your token.';
      } else if (error.response?.status === 404) {
        userErrorMessage = 'No faculty found for this organization.';
      } else if (error.message.includes('Network Error')) {
        userErrorMessage = 'Network error. Please check your connection.';
      }
      setFaculty([]);
      toast.error(userErrorMessage);
    } finally {
      setLoadingFaculty(false);
    }
  }, [axios, token, orgId]);

  const fetchCoursePlans = async () => {
    setLoadingCoursePlans(true);
    try {
      const courseIds = (updatedClass?.courseIds || []).filter(id => id && id !== 'string');
      if (courseIds.length === 0) {
        setCoursePlanData({});
        console.log('No course IDs provided, coursePlanData set to empty.');
        return {};
      }

      const coursePlansPromises = courseIds.map(async (courseId) => {
        try {
          const response = await axios.get(
            `${configData.GET_COURSE_PLAN_BY_COURSE}${courseId}`,
            {
              headers: {
                Authorization: token,
                accept: 'application/json',
              },
            }
          );

          console.log(`Course plan response for course ${courseId}:`, response.data);
          const filteredData = Array.isArray(response.data)
            ? response.data.filter(
              (plan) => plan.classId === updatedClass?.classId
            )
            : response.data.classId === updatedClass?.classId
              ? [response.data]
              : [];
          return { courseId, data: filteredData };
        } catch (error) {
          console.warn(`No course plan found for course ${courseId}:`, error.message);
          return { courseId, data: [] };
        }
      });

      const results = await Promise.all(coursePlansPromises);
      const planData = {};
      results.forEach(({ courseId, data }) => {
        planData[courseId] = data;
      });
      setCoursePlanData(planData);
      console.log('Course plan data set:', planData);
      return planData;
    } catch (error) {
      console.error('Error fetching course plans:', error);
      toast.error('Failed to load course plans.');
      return {};
    } finally {
      setLoadingCoursePlans(false);
    }
  };

  const assignFacultyToCourse = async (courseId, facultyId, isEditing = false) => {
    try {
      if (isEditing) {
        // Find the existing course plan
        const coursePlans = coursePlanDataRef.current[courseId] || [];
        const existingPlan = coursePlans.find(
          (plan) => plan.classId === updatedClass?.classId && plan.courseId === courseId
        );

        if (!existingPlan) {
          throw new Error('No existing course plan found for editing.');
        }

        // Construct payload for PUT request
        const payload = {
          createdBy: existingPlan.createdBy || null,
          createdAt: existingPlan.createdAt || new Date().toISOString(),
          lastUpdatedBy: 'admin',
          lastUpdatedAt: new Date().toISOString(),
          coursePlanId: existingPlan.coursePlanId,
          courseId: existingPlan.courseId,
          orgId: existingPlan.orgId || orgId,
          academicYear: existingPlan.academicYear || academicYear,
          classId: existingPlan.classId || updatedClass?.classId,
          facultyId: facultyId,
          subtopicsPlanList: existingPlan.subtopicsPlanList || [],
        };

        console.log('Updating course plan with payload:', {
          payload,
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
        });

        const response = await axios.put(
          configData.get_resources_resources_course_plan(existingPlan.coursePlanId),
          payload,
          {
            headers: {
              Authorization: token,
              'Content-Type': 'application/json',
              accept: 'application/json',
            },
          }
        );

        console.log('Faculty update response:', {
          status: response.status,
          data: response.data,
          headers: response.headers,
        });

        if (response.status === 200) {
          setCoursePlanData((prev) => ({
            ...prev,
            [courseId]: prev[courseId].map((plan) =>
              plan.coursePlanId === existingPlan.coursePlanId ? response.data : plan
            ),
          }));
          coursePlanDataRef.current = {
            ...coursePlanDataRef.current,
            [courseId]: coursePlanDataRef.current[courseId].map((plan) =>
              plan.coursePlanId === existingPlan.coursePlanId ? response.data : plan
            ),
          };
          return true;
        }
      } else {
        // Create new course plan
        const payload = {
          courseId: courseId,
          orgId: orgId,
          academicYear: academicYear,
          classId: updatedClass?.classId,
          facultyId: facultyId,
          subtopicsPlanList: [],
        };

        console.log('Assigning faculty to course with payload:', {
          payload,
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
        });

        const response = await axios.post(
          configData.RESOURCES_COURSE_PLAN,
          payload,
          {
            headers: {
              Authorization: token,
              'Content-Type': 'application/json',
              accept: 'application/json',
            },
          }
        );

        console.log('Faculty assignment response:', {
          status: response.status,
          data: response.data,
          headers: response.headers,
        });

        if (response.status === 201) {
          setCoursePlanData((prev) => ({
            ...prev,
            [courseId]: [...(prev[courseId] || []), response.data],
          }));
          coursePlanDataRef.current = {
            ...coursePlanDataRef.current,
            [courseId]: [...(coursePlanDataRef.current[courseId] || []), response.data],
          };
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error assigning/updating faculty to course:', {
        courseId,
        facultyId,
        isEditing,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  };

  const handleAssignFaculty = async (course) => {
    setSelectedCourseForFaculty({
      ...course,
      isEditing: false,
    });
    await fetchFaculties();
    setSelectedFacultyId('');
    setIsFacultyModalOpen(true);
  };

  const handleEditFaculty = async (course) => {
    setSelectedCourseForFaculty({
      ...course,
      isEditing: true,
    });
    await fetchFaculties();
    if (course.facultyIds && course.facultyIds.length > 0) {
      setSelectedFacultyId(course.facultyIds[0]);
    } else {
      setSelectedFacultyId('');
    }
    setIsFacultyModalOpen(true);
  };

  const handleFacultyModalClose = () => {
    setIsFacultyModalOpen(false);
    setSelectedCourseForFaculty(null);
    setSelectedFacultyId('');
    setFacultySearchTerm('');
  };

  const handleFacultyAssignmentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFacultyId) {
      toast.warning('Please select a faculty member.');
      return;
    }

    console.log('Selected Faculty ID before assignment:', selectedFacultyId);
    setLoading(true);
    try {
      const success = await assignFacultyToCourse(
        selectedCourseForFaculty.courseId,
        selectedFacultyId,
        selectedCourseForFaculty.isEditing
      );

      if (success) {
        const action = selectedCourseForFaculty.isEditing ? 'updated' : 'assigned';
        toast.success(`Faculty ${action} successfully!`);
        await fetchAssignedCourses();
        handleFacultyModalClose();
      } else {
        const action = selectedCourseForFaculty.isEditing ? 'updating' : 'assigning';
        toast.error(`Error ${action} faculty: Unknown error`);
      }
    } catch (error) {
      const action = selectedCourseForFaculty.isEditing ? 'updating' : 'assigning';
      console.error(`Error ${action} faculty:`, error.response?.data || error.message);
      toast.error(`Error ${action} faculty: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getFacultyName = (facultyId) => {
    if (!facultyId || facultyId === 'unknown') return 'No ID found';
    const facultyMember = faculty.find((f) => f.id === facultyId);
    return facultyMember
      ? `${facultyMember.firstName || ''} ${facultyMember.lastName || ''}`.trim() || 'No ID found'
      : 'No ID found';
  };

  const filteredFaculty = faculty.filter((facultyMember) => {
    const name = `${facultyMember.firstName || ''} ${facultyMember.lastName || ''}`.toLowerCase();
    const email = (facultyMember.email || '').toLowerCase();
    const searchLower = facultySearchTerm.toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  });

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${configData.GET_COURSES}/${orgId}`,
        {
          headers: {
            Authorization: token,
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );
      setCourses(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching courses:', error.response?.data || error.message);
      setCourses([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedCourses = async (updatedCourseIds = updatedClass?.courseIds || []) => {
    try {
      setLoading(true);

      const assignedCourseIds = (updatedCourseIds || []).filter(id => id && id !== 'string');
      if (assignedCourseIds.length === 0) {
        setCourseProgress([]);
        setClassStats({
          totalCourses: 0,
          totalTopics: 0,
          completedTopics: 0,
          overallProgress: 0,
        });
        console.log('No assigned course IDs, courseProgress cleared.');
        return;
      }

      console.log('Assigned course IDs:', assignedCourseIds);
      console.log('coursePlanData in fetchAssignedCourses:', coursePlanData);
      console.log('coursePlanDataRef in fetchAssignedCourses:', coursePlanDataRef.current);

      const courseProgressData = await Promise.all(
        assignedCourseIds.map(async (courseId) => {
          const courseDetails = await fetchCourseDetails(courseId);
          const fullCourse = courseDetails.fullResponse || {};
          const totalTopics = fullCourse.courseDetails?.topics?.length || 0;

          let coursePlanArray = coursePlanDataRef.current[courseId] || [];
          console.log(`Course ${courseId} coursePlanArray:`, coursePlanArray);

          if (coursePlanArray.length === 0 && !loadingCoursePlans) {
            console.log(`Retrying fetchCoursePlans for course ${courseId}`);
            const plans = await fetchCoursePlans();
            coursePlanDataRef.current = plans;
            setCoursePlanData(plans);
            coursePlanArray = coursePlanDataRef.current[courseId] || [];
            console.log(`Retry coursePlanDataRef for ${courseId}:`, coursePlanArray);
          }

          // Dynamically compute progress from the course plan Subtopic statuses
          const completedSubtopicNames = new Set();
          coursePlanArray.forEach((plan) => {
            if (plan.subtopicsPlanList) {
              plan.subtopicsPlanList.forEach((sub) => {
                if (sub.status === 'Completed') {
                  completedSubtopicNames.add(sub.subtopicName);
                }
              });
            }
          });
          const completedTopics = completedSubtopicNames.size;
          const dynamicProgress = totalTopics > 0 
            ? Math.floor((completedTopics / totalTopics) * 100)
            : 0;

          let status = 'On Track';
          let color = '#28a745';
          if (dynamicProgress === 0) {
             status = 'Not Started';
             color = '#6c757d';
          } else if (dynamicProgress < 30) {
            status = 'Significantly Behind';
            color = '#dc3545';
          } else if (dynamicProgress < 60) {
            status = 'Behind Schedule';
            color = '#ffc107';
          } else if (dynamicProgress >= 100) {
            status = 'Completed';
            color = '#28a745';
          } else {
            status = 'On Track';
            color = '#17a2b8';
          }

          const facultyIds = coursePlanArray
            .map((plan) => plan.facultyId)
            .filter((id) => id && id !== 'unknown');

          console.log(`Course ${courseId} facultyIds:`, facultyIds);

          const facultyNames = await Promise.all(
            facultyIds.map(async (id) => {
              const details = await fetchFacultyDetails(id);
              return details.name;
            })
          );

          console.log(`Course ${courseId} faculty assignment:`, { facultyIds, facultyNames });

          return {
            id: courseId,
            courseId: courseId,
            name: courseDetails.name || 'Unknown Course',
            code: courseDetails.code || '',
            description: fullCourse.courseDetails?.courseDescription || '',
            courseType: fullCourse.courseType || 'theory',
            progress: dynamicProgress,
            topicsCompleted: completedTopics,
            totalTopics: totalTopics,
            status: status,
            color: color,
            topics: fullCourse.courseDetails?.topics || [],
            assignedFaculty: facultyNames.length > 0 ? facultyNames.join(', ') : 'No faculty assigned',
            facultyIds: facultyIds,
          };
        })
      );

      setCourseProgress(courseProgressData);

      const totalCourses = courseProgressData.length;
      const totalTopics = courseProgressData.reduce(
        (sum, course) => sum + course.totalTopics,
        0
      );
      const completedTopics = courseProgressData.reduce(
        (sum, course) => sum + course.topicsCompleted,
        0
      );
      const overallProgress = totalTopics > 0 ? Math.round((completedTopics / totalTopics) * 100) : 0;

      setClassStats({
        totalCourses,
        totalTopics,
        completedTopics,
        overallProgress,
      });
      console.log('Course progress data set:', courseProgressData);
    } catch (error) {
      console.error('Error fetching assigned courses:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseDetails = async (courseId) => {
    if (courseDetailsCache[courseId]) {
      return courseDetailsCache[courseId];
    }

    try {
      const response = await axios.get(
        `${configData.GET_COURSE_BY_ID}/${courseId}`,
        {
          headers: {
            Authorization: token,
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      const courseName =
        response.data.courseDetails?.courseName || response.data.courseName || 'Unknown Course';
      const courseCode =
        response.data.courseDetails?.courseCode || response.data.courseCode || '';

      const courseDetails = {
        name: courseName,
        code: courseCode,
        displayName: courseCode ? `${courseName} (${courseCode})` : courseName,
        fullResponse: response.data,
      };

      setCourseDetailsCache((prev) => ({
        ...prev,
        [courseId]: courseDetails,
      }));

      return courseDetails;
    } catch (error) {
      console.error(`Error fetching course details for ${courseId}:`, error.response?.data || error.message);
      return {
        name: 'Unknown Course',
        code: '',
        displayName: 'Unknown Course',
        fullResponse: {},
      };
    }
  };

  const getCourseDisplayNames = async (courseIds) => {
    if (!courseIds || courseIds.length === 0) return '';

    try {
      const courseDetails = await Promise.all(
        courseIds.map((courseId) => fetchCourseDetails(courseId))
      );
      return courseDetails.map((detail) => detail.displayName).join(', ');
    } catch (error) {
      console.error('Error fetching course names:', error.response?.data || error.message);
      return 'Error loading courses';
    }
  };

  const handleEdit = async () => {
    await fetchCourses();
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const isCustomOrg = activeOrgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
    if (isCustomOrg) {
      if (!editClassName.trim()) {
        toast.warning('Please enter a valid class name.');
        return;
      }
    } else {
      if (!editClassName.trim() || !editSectionName.trim()) {
        toast.warning('Please enter valid class name and section name.');
        return;
      }
    }

    if (typeof setClasses !== 'function') {
      console.error('setClasses is not a function. Cannot update classes state.');
      toast.error('Error: Unable to update classes. Please contact support.');
      return;
    }

    setLoading(true);
    try {
      const updatedClassData = {
        classId: viewingClass.classId || viewingClass.id,
        className: editClassName.trim(),
        sectionName: isCustomOrg ? '' : editSectionName.trim(),
        orgId: orgId,
        courseIds: editSelectedCourses,
        academicYear: academicYear,
        classScheduleMap: null,
        durationMins: 40,
      };

      console.log('Edit Class Payload:', updatedClassData);

      const response = await axios.put(
        `${configData.GET_CLASS_BY_ID}/${viewingClass.classId || viewingClass.id}`,
        updatedClassData,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
        }
      );

      if (response.status === 200) {
        const courseDisplayNames = await getCourseDisplayNames(editSelectedCourses);
        const updatedClasses = classes.map((cls) =>
          (cls.classId || cls.id) === (viewingClass.classId || viewingClass.id)
            ? {
              ...cls,
              className: editClassName.trim(),
              sectionName: editSectionName.trim(),
              courseIds: editSelectedCourses,
              courseNames: courseDisplayNames,
              academicYear: academicYear,
            }
            : cls
        );
        setClasses(updatedClasses);
        setUpdatedClass({
          ...updatedClass,
          className: editClassName.trim(),
          sectionName: editSectionName.trim(),
          courseIds: editSelectedCourses,
          courseNames: courseDisplayNames,
          academicYear: academicYear,
        });
        setEditSelectedCourses(editSelectedCourses);
        setSelectedCourses(editSelectedCourses);
        await fetchAssignedCourses(editSelectedCourses);
        toast.success('Successfully updated class');
        setIsEditModalOpen(false);
        setEditClassName('');
        setEditSectionName('');
        setEditSelectedCourses([]);
        setEditCourseSearchTerm('');
      } else {
        console.error('Unexpected response status:', response.status, response.data);
        toast.error(`Failed to update class. Server responded with status ${response.status}.`);
      }
    } catch (error) {
      console.error('Error updating class:', error.response?.data || error.message);
      toast.error(
        `Error updating class: ${error.response?.data?.message || error.message || 'Unknown error'
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditClassName(viewingClass.className);
    setEditSectionName(viewingClass.sectionName);
    setEditSelectedCourses(viewingClass.courseIds || []);
    setEditCourseSearchTerm('');
  };

  const handleAssignCourses = async () => {
    await fetchCourses();
    setIsCourseModalOpen(true);
  };

  const handleAssignCoursesSubmit = async (e) => {
    e.preventDefault();

    if (!viewingClass.classId && !viewingClass.id) {
      toast.error('Error: Class ID is missing.');
      return;
    }

    if (selectedCourses.length === 0) {
      toast.warning('Please select at least one course.');
      return;
    }

    if (typeof setClasses !== 'function') {
      console.error('setClasses is not a function. Cannot update classes state.');
      toast.error('Error: Unable to update classes. Please contact support.');
      return;
    }

    setLoading(true);
    try {
      const updatedClassData = {
        classId: viewingClass.classId || viewingClass.id,
        className: viewingClass.className,
        sectionName: viewingClass.sectionName,
        orgId: orgId,
        courseIds: selectedCourses,
        academicYear: academicYear,
        classScheduleMap: null,
        durationMins: 40,
      };

      console.log('Assign Courses Payload:', updatedClassData);

      const response = await axios.put(
        `${configData.UPDATE_CLASS_SCHEDULE_BY_ID}/${viewingClass.classId || viewingClass.id}`,
        updatedClassData,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
        }
      );

      if (response.status === 200) {
        const courseDisplayNames = await getCourseDisplayNames(selectedCourses);
        setUpdatedClass({
          ...updatedClass,
          courseIds: selectedCourses,
          courseNames: courseDisplayNames,
          academicYear: academicYear,
        });
        setClasses((prevClasses) =>
          prevClasses.map((cls) =>
            (cls.classId || cls.id) === (viewingClass.classId || viewingClass.id)
              ? {
                ...cls,
                courseIds: selectedCourses,
                courseNames: courseDisplayNames,
                academicYear: academicYear,
              }
              : cls
          )
        );
        setSelectedCourses(selectedCourses);
        await fetchAssignedCourses(selectedCourses);
        toast.success(`Successfully assigned ${selectedCourses.length} course(s) to ${viewingClass.className} Section ${viewingClass.sectionName}`);
        setIsCourseModalOpen(false);
        setCourseSearchTerm('');
      } else {
        console.error('Unexpected response status:', response.status, response.data);
        toast.error(`Failed to assign courses. Server responded with status ${response.status}.`);
      }
    } catch (error) {
      console.error('Error assigning courses:', error.response?.data || error.message);
      toast.error(`Error assigning courses: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelection = (courseId) => {
    setSelectedCourses((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    );
  };

  const filteredCourses = courses.filter((course) => {
    const courseName = course.courseDetails?.courseName || course.courseName || '';
    const courseCode = course.courseDetails?.courseCode || course.courseCode || '';
    const searchLower = courseSearchTerm.toLowerCase();
    return (
      courseName.toLowerCase().includes(searchLower) ||
      courseCode.toLowerCase().includes(searchLower)
    );
  });

  const filteredEditCourses = courses.filter((course) => {
    const courseName = course.courseDetails?.courseName || course.courseName || '';
    const courseCode = course.courseDetails?.courseCode || course.courseCode || '';
    const searchLower = editCourseSearchTerm.toLowerCase();
    return (
      courseName.toLowerCase().includes(searchLower) ||
      courseCode.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Track':
        return '#28a745';
      case 'Behind Schedule':
        return '#ffc107';
      case 'Significantly Behind':
        return '#dc3545';
      case 'Ahead of Schedule':
        return '#17a2b8';
      default:
        return '#6c757d';
    }
  };

  const renderOverview = () => {
    if (loading || loadingCoursePlans) {
      return (
        <div className="tab-content">
          <div className="loading-message">Loading...</div>
        </div>
      );
    }

    return (
      <div className="tab-content">
        <div className="detail-section">
          <div className="cls-section-header">
            {userRole !== 'STUDENT' && (
              <button className="edit-class-btn" onClick={handleEdit} title="Edit">
                <FaEdit />
              </button>
            )}
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Class Name:</label>
              <span>{updatedClass.className}</span>
            </div>
            {!(activeOrgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83') && (
              <div className="detail-item">
                <label>Section Name:</label>
                <span>{updatedClass.sectionName}</span>
              </div>
            )}
          </div>
        </div>

        {!isSmallOrg && (
          <>
            <div className="stats-section">
              <div className="stats-grid">
                <div className="class-stat-card">
                  <div className="stat-number">{classStats.totalCourses}</div>
                  <div className="stat-label">Total Courses</div>
                </div>
                <div className="class-stat-card">
                  <div className="stat-number">{classStats.totalTopics}</div>
                  <div className="stat-label">Total Topics</div>
                </div>
                <div className="class-stat-card">
                  <div className="stat-number">{classStats.completedTopics}</div>
                  <div className="stat-label">Completed Topics</div>
                </div>
                <div className="class-stat-card">
                  <div className="stat-number">{classStats.overallProgress}%</div>
                  <div className="stat-label">Overall Progress</div>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <div className="section-header">
                <h3>Assigned Courses</h3>
                {userRole !== 'STUDENT' && (
                  <button className="assign-courses-btn" title="Assign Courses" onClick={handleAssignCourses}>
                    <FaPlus />
                  </button>
                )}
              </div>

              {courseProgress.length > 0 ? (
                <div className="courses-progress-list">
                  {courseProgress.map((course) => (
                    <div key={course.courseId} className="course-progress-item">
                      <div className="course-info">
                        <div className="course-header">
                          <h4 className="course-name">{course.name}</h4>
                          <div className="faculty-assignment">
                            <div className="faculty-info">
                              {course.facultyIds.some((id) => loadingFacultyNames[id]) ? (
                                <span className="loading-faculty">Loading faculty names...</span>
                              ) : (
                                <span className={`assigned-faculty-name ${course.facultyIds.length === 0 ? 'no-id' : ''}`}>
                                  {course.assignedFaculty}
                                </span>
                              )}
                            </div>
                            <div className="faculty-actions">
                              {userRole !== 'STUDENT' && (
                                course.facultyIds.length === 0 ? (
                                  <button
                                    className="assign-faculty-btn"
                                    onClick={() => handleAssignFaculty(course)}
                                    title="Assign Faculty"
                                  >
                                    <FaUserPlus />
                                  </button>
                                ) : (
                                  <button
                                    className="edit-faculty-btn"
                                    onClick={() => handleEditFaculty(course)}
                                    title="Change Faculty"
                                  >
                                    <FaEdit />
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="course-details">
                          <span className="course-code">{course.code}</span>
                          <span className="course-type">{course.courseType}</span>
                        </div>
                        <div className="course-detail-info">
                          <span>{course.description}</span>
                        </div>
                        <div className="course-topics">
                          <span>Topics:</span>
                          {course.topics.length > 0 ? (
                            <ul className="topics-list">
                              {course.topics.map((topic, idx) => (
                                <li key={idx}>{topic}</li>
                              ))}
                            </ul>
                          ) : (
                            <span>No topics available</span>
                          )}
                        </div>
                      </div>
                      <div className="progress-section">
                        <div className="progress-header">
                          <span className="progress-percentage">{course.progress}%</span>
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${course.progress}%`,
                              backgroundColor: course.color,
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-courses">
                  <p>No courses assigned to this class section.</p>
                </div>
              )}
            </div>
          </>
        )}

        {isFacultyModalOpen && selectedCourseForFaculty && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>
                  {selectedCourseForFaculty.isEditing ? 'Change Faculty for' : 'Assign Faculty to'} {selectedCourseForFaculty.name}
                </h3>
                <button className="modal-close-btn" onClick={handleFacultyModalClose}>
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleFacultyAssignmentSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Course:</label>
                    <div className="selected-course-info">
                      <strong>{selectedCourseForFaculty.name}</strong>
                      {selectedCourseForFaculty.code && (
                        <span> ({selectedCourseForFaculty.code})</span>
                      )}
                    </div>
                  </div>
                  {selectedCourseForFaculty.isEditing && selectedCourseForFaculty.assignedFaculty &&
                    selectedCourseForFaculty.assignedFaculty !== 'No faculty assigned' && (
                      <div className="form-group">
                        <label>Current Faculty:</label>
                        <div className="current-faculty-info">
                          <span className="current-faculty-name">{selectedCourseForFaculty.assignedFaculty}</span>
                        </div>
                      </div>
                    )}
                  <div className="form-group">
                    <label htmlFor="facultySearch">Search Faculty:</label>
                    <input
                      type="text"
                      id="facultySearch"
                      onChange={(e) => handleFacultySearchChange(e.target.value)}
                      placeholder="Search by name or email"
                      className="faculty-search-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Select Faculty:</label>
                    {loadingFaculty ? (
                      <div className="loading-message">Loading faculty...</div>
                    ) : filteredFaculty.length === 0 ? (
                      <p>No faculty found matching your search.</p>
                    ) : (
                      <div className="faculty-list">
                        {filteredFaculty.map((facultyMember) => (
                          <div key={facultyMember.id} className="faculty-radio">
                            <input
                              type="radio"
                              id={`faculty-${facultyMember.id}`}
                              name="selectedFaculty"
                              value={facultyMember.id}
                              checked={selectedFacultyId === facultyMember.id}
                              onChange={(e) => {
                                console.log('Selected faculty ID:', e.target.value);
                                setSelectedFacultyId(e.target.value);
                              }}
                            />
                            <label htmlFor={`faculty-${facultyMember.id}`}>
                              <div className="faculty-info">
                                <div className="faculty-name">
                                  <strong>
                                    {`${facultyMember.firstName || ''} ${facultyMember.lastName || ''}`.trim()}
                                  </strong>
                                </div>
                                {facultyMember.email && (
                                  <div className="faculty-email">{facultyMember.email}</div>
                                )}
                                {facultyMember.designation && (
                                  <div className="faculty-designation">{facultyMember.designation}</div>
                                )}
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleFacultyModalClose}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="save-btn" disabled={loading || !selectedFacultyId}>
                    {loading ?
                      (selectedCourseForFaculty.isEditing ? 'Updating...' : 'Assigning...') :
                      (selectedCourseForFaculty.isEditing ? 'Update Faculty' : 'Assign Faculty')
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTimetable = () => (
    <div className="tab-content">
      <Timetable
        className={updatedClass.className}
        sectionName={updatedClass.sectionName}
        classId={updatedClass.classId}
        token={token}
        axios={axios}
        assignedCourses={courseProgress}
        orgId={activeOrgId}
      />
    </div>
  );

  const renderPlanning = () => (
    <div className="tab-content">
      <Planning classId={updatedClass.classId} orgId={orgId} />
    </div>
  );

  const renderClassProgress = () => (
    <div className="tab-content">
      <p>Class progress content goes here.</p>
    </div>
  );

  const renderDocuments = () => (
    <div className="tab-content">
      <AcademicDocument classId={updatedClass.classId || updatedClass.id} token={token} axios={axios} orgId={activeOrgId} />
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'timetable':
        return renderTimetable();
      case 'planning':
        return renderPlanning();
      case 'progress':
        return renderClassProgress();
      case 'documents':
        return renderDocuments();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="class-details-container">
      <div className="class-details-header">
        <button className="back-btn" onClick={onBack} title="Back to Classes">
          <FaArrowLeft />
        </button>
        <div className="class-title">
          <h2>{updatedClass.className} {!(activeOrgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83') && updatedClass.sectionName}</h2> (<span>{academicYear}</span>)
        </div>
      </div>

      <div className="class-details-tabs">
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <FaInfoCircle /> Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'timetable' ? 'active' : ''}`}
            onClick={() => setActiveTab('timetable')}
          >
            <FaCalendarAlt /> Class Schedule
          </button>
          <button
            className={`tab-button ${activeTab === 'planning' ? 'active' : ''}`}
            onClick={() => setActiveTab('planning')}
          >
            <FaClipboardList /> Planning
          </button>
          <button
            className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            <FaFileAlt /> Documents
          </button>
          {/* <button
            className={`tab-button ${activeTab === 'progress' ? 'active' : ''}`}
            onClick={() => setActiveTab('progress')}
          >
            <FaChartLine /> Class Progress
          </button> */}
        </div>

        <div className="tab-content-wrapper">{renderTabContent()}</div>
      </div>

      {isEditModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Class</h3>
              <button className="modal-close-btn" onClick={handleEditModalClose}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="editClassName">Class Name:</label>
                  <input
                    type="text"
                    id="editClassName"
                    value={editClassName}
                    onChange={(e) => setEditClassName(e.target.value)}
                    placeholder="Enter class name"
                    required
                  />
                </div>
                {!(activeOrgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83') && (
                  <div className="form-group">
                    <label htmlFor="editSectionName">Section Name:</label>
                    <input
                      type="text"
                      id="editSectionName"
                      value={editSectionName}
                      onChange={(e) => setEditSectionName(e.target.value)}
                      placeholder="Enter section name"
                      required
                    />
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="editAcademicYear">Academic Year:</label>
                  <input
                    type="text"
                    id="editAcademicYear"
                    value={academicYear}
                    onChange={(e) => setAcademicYear(e.target.value)}
                    placeholder="Enter academic year (e.g., 2025-2026)"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Assigned Courses:</label>
                  <input
                    type="text"
                    id="editCourseSearch"
                    value={editCourseSearchTerm}
                    onChange={(e) => setEditCourseSearchTerm(e.target.value)}
                    placeholder="Search by course name or code"
                    className="course-search-input"
                  />
                  {loading ? (
                    <p>Loading courses...</p>
                  ) : filteredEditCourses.length === 0 ? (
                    <p>No courses found matching your search.</p>
                  ) : (
                    filteredEditCourses.map((course) => (
                      <div key={course.courseId} className="course-checkbox">
                        <input
                          type="checkbox"
                          id={`edit-course-${course.courseId}`}
                          checked={editSelectedCourses.includes(course.courseId)}
                          onChange={() =>
                            setEditSelectedCourses((prev) =>
                              prev.includes(course.courseId)
                                ? prev.filter((id) => id !== course.courseId)
                                : [...prev, course.courseId]
                            )
                          }
                        />
                        <label htmlFor={`edit-course-${course.courseId}`}>
                          {course.courseDetails?.courseName || course.courseName}
                          {course.courseDetails?.courseCode || course.courseCode
                            ? ` (${course.courseDetails?.courseCode || course.courseCode})`
                            : ''}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={handleEditModalClose}>
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isCourseModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                Assign Courses to {updatedClass.className} Section {updatedClass.sectionName}
              </h3>
              <button className="modal-close-btn" onClick={() => setIsCourseModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAssignCoursesSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <input
                    type="text"
                    id="courseSearch"
                    value={courseSearchTerm}
                    onChange={(e) => setCourseSearchTerm(e.target.value)}
                    placeholder="Search by course name or code"
                    className="course-search-input"
                  />
                </div>
                {loading ? (
                  <div className="loading-message">Loading courses...</div>
                ) : filteredCourses.length === 0 ? (
                  <p>No courses found matching your search.</p>
                ) : (
                  <div className="courses-list">
                    {filteredCourses.map((course) => (
                      <div key={course.courseId} className="course-checkbox">
                        <input
                          type="checkbox"
                          id={`course-${course.courseId}`}
                          checked={selectedCourses.includes(course.courseId)}
                          onChange={() => handleCourseSelection(course.courseId)}
                        />
                        <label htmlFor={`course-${course.courseId}`}>
                          <div className="course-checkbox-content">
                            <div className="course-checkbox-header">
                              <strong>
                                {course.courseDetails?.courseName || course.courseName || 'Unknown Course'}
                                {course.courseDetails?.courseCode || course.courseCode
                                  ? ` (${course.courseDetails?.courseCode || course.courseCode})`
                                  : ''}
                              </strong>
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setIsCourseModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={loading}>
                  {loading ? 'Assigning...' : 'Assign Courses'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default ClassDetails;
